import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, ChevronRight, Plus, Minus } from "lucide-react";
import { Logo } from "../../components/Logo";

// Set a per-page <title> and meta description for SEO, restoring the previous
// values when the visitor navigates away.
function useSeo(title, description) {
  useEffect(() => {
    if (!title && !description) return;
    const prevTitle = document.title;
    let tag = document.querySelector('meta[name="description"]');
    const created = !tag;
    if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "description"); document.head.appendChild(tag); }
    const prevDesc = tag.getAttribute("content");
    if (title) document.title = title;
    if (description) tag.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      if (created) { tag.remove(); } else if (prevDesc != null) { tag.setAttribute("content", prevDesc); }
    };
  }, [title, description]);
}

// ─── Reusable marketing template for a single feature ────────────────────────
// Layout (approved): split hero → alternating text/visual rows → how-it-works
// strip → closing CTA → slim footer. Every feature page passes data + on-brand
// CSS mockups; the Navbar is rendered globally by the app Shell.
//
// Props:
//   accent        brand hex for this feature (headline accent, chips, steps)
//   eyebrow       mono uppercase tag above the headline
//   title         headline (plain part)
//   titleAccent   headline (accented, coloured part) — optional
//   subtitle      hero subcopy
//   heroChips     short trust bullets under the hero CTAs — string[]
//   heroVisual    JSX mockup shown on the right of the hero
//   rows          [{ tag, title, body, bullets?, visual, reverse? }]
//   steps         [{ title, body }]  — numbered how-it-works
//   useCases      [{ icon, persona, body }]  — "who it's for" cards
//   faqs          [{ q, a }]  — expandable FAQ (SEO + objections)
//   ctaTitle      closing CTA headline
//   ctaSubtitle   closing CTA subcopy
//   related       [{ key, label, to, desc, icon, accent }] — "explore more" grid
//   signupRole    "candidate" | "company" — sets /signup?role=… on primary CTAs
//   secondaryCta  { label, href } — optional 2nd CTA (mailto/http → <a>, else <Link>);
//                 shown in hero + closing CTA. Defaults: hero "See how it works",
//                 closing "Log in".
//   seoTitle      per-page <title>
//   seoDescription per-page meta description

// Renders a link that is a plain <a> for external/mailto hrefs and a router <Link>
// for internal routes, so a "Book a demo" mailto and a "/login" both work.
function CtaLink({ href, className, children }) {
  const external = /^(https?:|mailto:|tel:)/i.test(href || "");
  return external
    ? <a href={href} className={className}>{children}</a>
    : <Link to={href} className={className}>{children}</Link>;
}

export default function FeaturePage({
  accent = "#0025D8",
  eyebrow,
  title,
  titleAccent,
  subtitle,
  heroChips = [],
  heroVisual,
  rows = [],
  steps = [],
  useCases = [],
  faqs = [],
  ctaTitle,
  ctaSubtitle,
  related = [],
  signupRole = "candidate",
  secondaryCta = null,
  seoTitle,
  seoDescription,
}) {
  const signupTo = `/signup?role=${signupRole}`;
  // Marketing pages read top-down — start every visit at the top.
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useSeo(seoTitle, seoDescription);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main className="bg-white text-foreground">
      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-white to-[#F4F7FB]">
        <div
          className="pointer-events-none absolute -top-32 -right-32 w-[42rem] h-[42rem] rounded-full opacity-60"
          style={{ background: `radial-gradient(circle, ${accent}1A 0%, rgba(124,58,237,0.06) 40%, transparent 70%)` }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white border shadow-sm px-3.5 py-1.5 rounded-full mb-7"
              style={{ borderColor: `${accent}26` }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
              <span className="font-mono text-[11px] font-semibold tracking-wider" style={{ color: accent }}>{eyebrow}</span>
            </div>

            <h1 className="font-display font-black text-4xl md:text-[3.4rem] tracking-tight leading-[1.05] mb-6 text-[#0F172A]">
              {title}
              {titleAccent && <><br /><span style={{ color: accent }}>{titleAccent}</span></>}
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed mb-9 max-w-lg">{subtitle}</p>

            <div className="flex gap-3 flex-wrap mb-9">
              <Link
                to={signupTo}
                className="group inline-flex items-center gap-2 text-white px-7 py-4 rounded-xl font-semibold text-sm shadow-lg transition-all hover:brightness-110"
                style={{ background: accent, boxShadow: `0 10px 22px ${accent}33` }}
              >
                Get started free
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <CtaLink
                href={secondaryCta ? secondaryCta.href : "#how"}
                className="inline-flex items-center gap-2 bg-white border border-border text-foreground px-6 py-4 rounded-xl font-semibold text-sm hover:bg-secondary transition-colors"
              >
                {secondaryCta ? secondaryCta.label : "See how it works"}
              </CtaLink>
            </div>

            {heroChips.length > 0 && (
              <div className="flex gap-x-6 gap-y-2 flex-wrap pt-6 border-t border-border">
                {heroChips.map((x) => (
                  <div key={x} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CheckCircle2 className="w-3.5 h-3.5" style={{ color: accent }} /> {x}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl blur-2xl"
              style={{ background: `linear-gradient(to top right, ${accent}1A, rgba(124,58,237,0.10))` }} />
            <div className="relative">{heroVisual}</div>
          </div>
        </div>
      </section>

      {/* ── Alternating capability rows ── */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center py-12 md:py-16">
            <div className={r.reverse ? "lg:order-2" : ""}>
              {r.tag && (
                <div className="font-mono text-[10px] tracking-[0.18em] font-bold mb-3" style={{ color: accent }}>{r.tag}</div>
              )}
              <h2 className="font-display font-black text-3xl md:text-[2.4rem] tracking-tight text-[#0F172A] leading-[1.1] mb-4">{r.title}</h2>
              <p className="text-muted-foreground leading-relaxed text-base mb-5">{r.body}</p>
              {r.bullets && (
                <ul className="space-y-2.5">
                  {r.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-[#0F172A]">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accent }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={r.reverse ? "lg:order-1" : ""}>{r.visual}</div>
          </div>
        ))}
      </section>

      {/* ── How it works ── */}
      {steps.length > 0 && (
        <section id="how" className="bg-[#F4F7FB] border-y border-border py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase mb-3" style={{ color: accent }}>How it works</p>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[#0F172A] leading-tight">
                Up and running in minutes.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {steps.map((s, i) => (
                <div key={i} className="relative rounded-2xl border border-border bg-white p-7">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-lg mb-5"
                    style={{ background: `${accent}14`, color: accent }}>
                    {i + 1}
                  </div>
                  <h3 className="font-display font-bold text-lg mb-2 text-[#0F172A]">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Who it's for ── */}
      {useCases.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase mb-3" style={{ color: accent }}>Who it's for</p>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[#0F172A] leading-tight">
              Built for wherever you are.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {useCases.map((u, i) => (
              <div key={i} className="rounded-2xl border border-border bg-white p-7 hover:border-[#0025D8]/20 hover:shadow-lg hover:shadow-[#0F172A]/5 transition-all">
                {u.icon && (
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: `${accent}14`, color: accent }}>
                    <u.icon className="w-5 h-5" />
                  </div>
                )}
                <h3 className="font-display font-bold text-lg mb-2 text-[#0F172A]">{u.persona}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{u.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── FAQ ── */}
      {faqs.length > 0 && (
        <section className="bg-[#F4F7FB] border-y border-border py-20 md:py-24">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase mb-3" style={{ color: accent }}>FAQ</p>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[#0F172A] leading-tight">
                Questions, answered.
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={i} className="rounded-2xl border border-border bg-white overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                      aria-expanded={open}
                    >
                      <span className="font-semibold text-sm text-[#0F172A]">{f.q}</span>
                      {open
                        ? <Minus className="w-4 h-4 flex-shrink-0" style={{ color: accent }} />
                        : <Plus className="w-4 h-4 flex-shrink-0 text-muted-foreground" />}
                    </button>
                    {open && (
                      <div className="px-5 pb-5 -mt-1 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Explore more features ── */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase mb-3 text-muted-foreground">Explore more</p>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[#0F172A] leading-tight">
              The rest of your toolkit.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {related.map((f) => {
              if (f.comingSoon) {
                return (
                  <div key={f.key} className="relative rounded-2xl border border-dashed border-border bg-secondary/30 p-6 select-none">
                    <span className="absolute top-4 right-4 font-mono text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{ background: `${f.accent}1A`, color: f.accent }}>Coming soon</span>
                    {f.icon && (
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 bg-secondary text-muted-foreground">
                        <f.icon className="w-5 h-5" />
                      </div>
                    )}
                    <h3 className="font-display font-bold text-base mb-1.5 text-muted-foreground">{f.label}</h3>
                    <p className="text-sm text-muted-foreground/70 leading-relaxed">{f.desc}</p>
                  </div>
                );
              }
              return (
                <Link key={f.key} to={f.to}
                  className="group rounded-2xl border border-border bg-white p-6 hover:border-[#0025D8]/30 hover:shadow-xl hover:shadow-[#0F172A]/5 hover:-translate-y-1 transition-all block">
                  {f.icon && (
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: `${f.accent}14`, color: f.accent }}>
                      <f.icon className="w-5 h-5" />
                    </div>
                  )}
                  <h3 className="font-display font-bold text-base mb-1.5 text-[#0F172A] group-hover:text-[#0025D8] transition-colors">{f.label}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{f.desc}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: f.accent }}>
                    Learn more <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Closing CTA ── */}
      <section className="relative overflow-hidden py-20 md:py-24 text-white text-center" style={{ background: accent }}>
        <div
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[50rem] h-[50rem] rounded-full opacity-50"
          style={{ background: "radial-gradient(circle, rgba(124,58,237,0.35) 0%, transparent 60%)" }}
        />
        <div className="relative max-w-2xl mx-auto px-6">
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-5 leading-[1.08]">{ctaTitle}</h2>
          {ctaSubtitle && <p className="text-white/75 text-base mb-9">{ctaSubtitle}</p>}
          <div className="flex gap-3 justify-center flex-wrap">
            <Link
              to={signupTo}
              className="inline-flex items-center gap-2 bg-white px-8 py-4 rounded-xl font-bold text-base shadow-xl transition-colors hover:bg-white/90"
              style={{ color: accent }}
            >
              Get started free <ArrowUpRight className="w-5 h-5" />
            </Link>
            <CtaLink
              href={secondaryCta ? secondaryCta.href : "/login"}
              className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-xl font-semibold text-base transition-colors"
            >
              {secondaryCta ? secondaryCta.label : "Log in"}
            </CtaLink>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
            <div className="flex items-center gap-3">
              <Logo className="h-9 w-auto" />
              <span className="font-mono text-[10px] text-muted-foreground">© 2026</span>
            </div>
            <div className="flex gap-5">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Log in</Link>
              <Link to={signupTo} className="text-sm text-[#0025D8] font-semibold hover:underline">Sign up free →</Link>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}

// ─── Shared mockup chrome ────────────────────────────────────────────────────
// A browser/app "window" frame used to wrap CSS mockups so every visual reads
// as a product screenshot without needing image assets.
export function MockWindow({ label, badge, children, accent = "#0025D8" }) {
  return (
    <div className="relative rounded-2xl border border-border bg-white shadow-2xl shadow-[#0F172A]/10 overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-secondary/40">
        <span className="font-mono text-[10px] text-muted-foreground tracking-[0.15em]">{label}</span>
        {badge && (
          <span className="font-mono text-[10px] font-semibold" style={{ color: accent }}>{badge}</span>
        )}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// A labelled score bar reused across pages.
export function ScoreBar({ label, value, accent = "#0025D8", suffix = "/100" }) {
  return (
    <div className="rounded-xl p-4 border" style={{ borderColor: `${accent}33`, background: `${accent}0D` }}>
      <div className="font-mono text-[10px] tracking-[0.15em] mb-2" style={{ color: accent }}>{label}</div>
      <div className="flex items-end gap-2">
        <span className="font-display font-black text-3xl" style={{ color: accent }}>{value}</span>
        <span className="text-xs text-muted-foreground mb-1 font-mono">{suffix}</span>
      </div>
      <div className="mt-2.5 h-1.5 rounded-full w-full overflow-hidden" style={{ background: `${accent}26` }}>
        <div className="h-1.5 rounded-full" style={{ width: `${value}%`, background: accent }} />
      </div>
    </div>
  );
}
